import { useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { z } from 'zod'
import { ArrowLeft, Save } from 'lucide-react'

import type { CreateOutNoteRequest } from '@/lib/api/product-lot-api'
import type { ProductLotOption } from '@/components/product-lots/ProductLotItemsField'
import { productLotApi } from '@/lib/api/product-lot-api'
import { StickyFormFooter } from '@/components/common/StickyFormFooter'
import { ProductLotItemsField } from '@/components/product-lots/ProductLotItemsField'
import {
    ScanLotCodeButton,
    makeLotIdParser,
} from '@/components/lots/scan-lot-code-button'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { useCreateProductLotOutNote } from '@/hooks/use-product-lot-mutation'
import { useProductLotsQuery } from '@/hooks/use-product-lot-query'
import { useDebounced } from '@/hooks/use-debounced'
import { productLotToOption } from '@/components/product-lots/product-lot-option'
import { m } from '@/lib/paraglide/messages'

const itemSchema = z.object({
    kind: z.literal('existing'),
    product_lot_id: z
        .string({ message: m.form_err_pick_existing_lot() })
        .min(1, m.form_err_pick_existing_lot()),
    quantity: z
        .number({ message: m.form_err_quantity_required() })
        .positive(m.form_err_quantity_positive()),
})

const schema = z.object({
    reason: z.enum(['for_sale', 'discard', 'adjustment']),
    notes: z.string().max(2000, m.form_err_notes_too_long()).optional(),
    items: z.array(itemSchema).min(1, m.form_err_items_min_one()),
})

type FormData = z.infer<typeof schema>

type ProductLotStockOutFormProps = {
    mode: 'create' | 'edit'
    title: string
    submitLabel: string
    isPending: boolean
    defaultValues: FormData
    onSubmit: (req: CreateOutNoteRequest) => Promise<void>
    onCancel: () => void
    // Lô đã chọn khi sửa có thể ngoài top-50 → seed để Combobox hiển thị nhãn.
    seedLotOptions?: ProductLotOption[]
}

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/sales-warehouse/stock-out-notes/new',
)({
    component: NewProductLotOutNotePage,
})

function NewProductLotOutNotePage() {
    const navigate = useNavigate()
    const create = useCreateProductLotOutNote()

    const handleSubmit = async (req: CreateOutNoteRequest) => {
        const res = await create.mutateAsync(req)
        const noteId = (res as { data?: { id?: string } }).data?.id
        if (noteId) {
            void navigate({
                to: '/sales-warehouse/stock-out-notes/$id',
                params: { id: noteId },
                replace: true,
            })
        }
    }

    return (
        <ProductLotStockOutForm
            mode="create"
            title={m.pll_new_pxk_title()}
            submitLabel={m.sl_new_submit_pxk()}
            isPending={create.isPending}
            defaultValues={{
                reason: 'discard',
                notes: '',
                items: [
                    {
                        kind: 'existing' as const,
                        product_lot_id: '',
                        quantity: 0,
                    },
                ],
            }}
            onSubmit={handleSubmit}
            onCancel={() =>
                void navigate({
                    to: '/sales-warehouse/stock-out-notes',
                    search: { page: 1 },
                })
            }
        />
    )
}

export function ProductLotStockOutForm({
    mode,
    title,
    submitLabel,
    isPending,
    defaultValues,
    onSubmit,
    onCancel,
    seedLotOptions = [],
}: ProductLotStockOutFormProps) {
    const isEdit = mode === 'edit'

    const [lotSearch, setLotSearch] = useState('')
    const debouncedSearch = useDebounced(lotSearch)
    const { data: lotsData } = useProductLotsQuery({
        per_page: 50,
        only_available: '1',
        search: debouncedSearch || undefined,
    })

    const lotOptions = useMemo(() => {
        const fetched = (lotsData?.data ?? []).map(productLotToOption)
        return mergeLotOptions(seedLotOptions, fetched)
    }, [lotsData, seedLotOptions])

    const [scannedLots, setScannedLots] = useState<
        Map<string, ProductLotOption>
    >(new Map())
    const injectedScannedLots = useMemo(
        () => Array.from(scannedLots.values()),
        [scannedLots],
    )
    const fetchAndCacheLot = async (lotId: string) => {
        if (scannedLots.has(lotId)) return
        try {
            const res = await productLotApi.getLot(lotId)
            const l = res.data
            if (!l) return
            setScannedLots((prev) => {
                if (prev.has(lotId)) return prev
                const next = new Map(prev)
                next.set(lotId, productLotToOption(l))
                return next
            })
        } catch {
            // ignore — user vẫn thấy được row với ID trong form state.
        }
    }

    const form = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues,
    })

    const submit = form.handleSubmit(async (data) => {
        try {
            await onSubmit({
                reason: data.reason,
                notes: data.notes || undefined,
                items: data.items.map((it) => ({
                    product_lot_id: it.product_lot_id,
                    quantity: Number(it.quantity),
                })),
            })
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_new_error_create(),
            )
        }
    })

    return (
        <div className="space-y-4 p-4 pb-24 md:p-6 md:pb-6">
            <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={onCancel}>
                    <ArrowLeft className="size-4 mr-1" /> {m.sl_lot_back()}
                </Button>
                <h1 className="text-xl font-semibold">{title}</h1>
            </div>

            <FormProvider {...form}>
                <form
                    onSubmit={submit}
                    className="space-y-3 md:space-y-6 max-w-5xl"
                >
                    <div className="grid gap-4 md:grid-cols-2 rounded-md border bg-card p-4">
                        <div>
                            <Label>{m.sl_new_reason_out()}</Label>
                            <Select
                                value={form.watch('reason')}
                                disabled={isEdit}
                                onValueChange={(v) =>
                                    form.setValue(
                                        'reason',
                                        v as FormData['reason'],
                                    )
                                }
                            >
                                <SelectTrigger className="w-full mt-1">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="discard">
                                        {m.pll_out_reason_discard()}
                                    </SelectItem>
                                    <SelectItem value="adjustment">
                                        {m.pll_out_reason_adjustment()}
                                    </SelectItem>
                                    {/* PXK for_sale sinh từ luồng đơn hàng — chỉ hiện
                                        khi đang sửa đúng phiếu đó để Select render nhãn. */}
                                    {form.watch('reason') === 'for_sale' && (
                                        <SelectItem value="for_sale">
                                            {m.pll_out_reason_for_sale()}
                                        </SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>{m.sl_new_notes()}</Label>
                            <textarea
                                {...form.register('notes')}
                                placeholder={m.sl_new_notes_placeholder()}
                                rows={2}
                                className="mt-1 w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm"
                            />
                        </div>
                    </div>

                    <ProductLotItemsField
                        mode="out"
                        productOptions={[]}
                        warehouseOptions={[]}
                        lotOptions={lotOptions}
                        scannedLots={injectedScannedLots}
                        onLotSearchChange={setLotSearch}
                        headerAction={
                            <ScanLotCodeButton
                                parseLotId={makeLotIdParser(
                                    'sales-warehouse/lots',
                                )}
                                onScanned={(lotId) => {
                                    const current =
                                        form.getValues('items') ?? []
                                    form.setValue(
                                        'items',
                                        [
                                            ...current,
                                            {
                                                kind: 'existing',
                                                product_lot_id: lotId,
                                                quantity: 0,
                                            },
                                        ],
                                        {
                                            shouldDirty: true,
                                            shouldValidate: false,
                                        },
                                    )
                                    void fetchAndCacheLot(lotId)
                                }}
                            />
                        }
                    />

                    {form.formState.errors.items?.message && (
                        <p className="text-sm text-destructive">
                            {form.formState.errors.items.message}
                        </p>
                    )}

                    <StickyFormFooter>
                        <Button
                            type="button"
                            variant="outline"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            onClick={onCancel}
                        >
                            {m.sl_new_cancel()}
                        </Button>
                        <Button
                            type="submit"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            disabled={isPending}
                        >
                            <Save className="size-4 mr-1" />
                            {isPending ? m.sl_new_submitting() : submitLabel}
                        </Button>
                    </StickyFormFooter>
                </form>
            </FormProvider>
        </div>
    )
}

// mergeLotOptions ghép option seed (lô đã chọn khi sửa) với option fetch, dedupe
// theo value, ưu tiên nhãn từ kết quả fetch (số khả dụng mới hơn).
function mergeLotOptions(
    seed: ProductLotOption[],
    fetched: ProductLotOption[],
): ProductLotOption[] {
    const byValue = new Map<string, ProductLotOption>()
    for (const o of seed) byValue.set(o.value, o)
    for (const o of fetched) byValue.set(o.value, o)
    return Array.from(byValue.values())
}
